import { useDispatch, useSelector } from "react-redux";
import { useCallback, useState, useRef, useEffect, useMemo } from "react";
import { fabric } from 'fabric';
import { TwoDMesh, actions as builderActions } from "@/store/builderSlice";
import BuilderSelect from "@/store/builderStoreSelector";
import TwoDWindowDoorRenderer from "./TwoDWindowDoorRenderer";
import { getCenterOfTiltedLine, getParallelLine, getPointAtDistanceAndAngle } from "@/utils/builder/twoDRoomLayoutUtils";

export interface TwoDWallRendererProps {
    canvas: fabric.Canvas,
    wall: TwoDMesh,
    unitsScale: number,
    xOffset: number,
    yOffset: number
}

const TwoDWallRenderer: React.FC<TwoDWallRendererProps> = ({ canvas, wall, unitsScale, xOffset, yOffset }
) => {
    const dispatch = useDispatch();
    const updatedWalls: TwoDMesh[] = BuilderSelect.twoDUi().walls;
    const deletedWindowOrDoorId: string = BuilderSelect.twoDUi().deletedWindowOrDoorId;
    const currentWindowOrDoorId: string = BuilderSelect.twoDRoomLayout().activeWindowOrDoorId;
    const wallContainerRect = useRef<fabric.Object | undefined>(undefined);
    const textRef = useRef<fabric.Text | undefined>(undefined);
    const lineRef = useRef<fabric.Line | undefined>(undefined);
    const leftArrowRef = useRef<fabric.Triangle | undefined>(undefined);
    const rightArrowRef = useRef<fabric.Triangle | undefined>(undefined);
    const counter = useRef(0);
    counter.current = counter.current + 1;
    const updatedWall = updatedWalls.find(uw => uw.id === wall.id);
    const currentWall = updatedWall || wall;

    const center = {
        x: (currentWall.position.x - xOffset) * unitsScale,
        y: (currentWall.position.y - yOffset) * unitsScale
    };

    useEffect(() => {
        if (deletedWindowOrDoorId !== '' && deletedWindowOrDoorId != null) {
            const objects = canvas.getObjects();
            const objToDelete = objects.find(obj => obj.name === 'window_door_' + deletedWindowOrDoorId);
            if (objToDelete)
                canvas.remove(objToDelete);

        }
    }, [deletedWindowOrDoorId])
    const onWallSelected = useCallback(() => {
        console.log('canvas- wall selected - ', currentWall.id);
        wallContainerRect.current!.strokeWidth = 3;
        wallContainerRect.current!.stroke = '#16A085';
        //wallContainerRect.current!.opacity = 0.5;
        canvas.requestRenderAll();
        const updatedCenter = getPointAtDistanceAndAngle(center, 5, (currentWall.rotation + 90) % 135);
        dispatch(builderActions.updateActiveSelection({
            screenCoords: {
                x: updatedCenter.x + canvas.viewportTransform![4] || 0,
                y: updatedCenter.y + canvas.viewportTransform![5] || 0
            }
        }));
        dispatch(builderActions.updateActiveWallId({
            activeWallId: currentWall.id
        }));
        dispatch(builderActions.updateTwoDUi({
            showWallCtxMenu: true
        }));
    }, []);

    const initEvents = (rect: fabric.Object) => {
        rect.on('selected', onWallSelected);
        rect.on('deselected', () => {
            rect.strokeWidth = 0;
            //rect.opacity = 0.1;
            canvas.requestRenderAll();
            dispatch(builderActions.updateActiveWallId({
                activeWallId: ''
            }));
        });
    }

    const updatedCenter = getPointAtDistanceAndAngle(center, 5, (currentWall.rotation + 90) % 135);
    const textPos = { ...updatedCenter };
    if (currentWall.id === 'wall0') textPos.y -= 20;
    if (currentWall.id === 'wall1') textPos.x += 40;
    if (currentWall.id === 'wall2') textPos.y += 40;
    if (currentWall.id === 'wall3') textPos.x -= 40;
    const wallWidthFt = Math.floor(currentWall.width);
    const wallWidthInches = Math.floor((currentWall.width - wallWidthFt) * 12);
    const wallWidthInchesStr = wallWidthInches > 0 ? `.${wallWidthInches}` : '';

    let lineOffset = 10; //20
    if (currentWall.id === 'wall2') lineOffset = -20;
    if (currentWall.id === 'wall1') {
        if (currentWall.rotation < 0) lineOffset = -30;
    }
    if (currentWall.id === 'wall3') {
        if (currentWall.rotation > 0) lineOffset = -20;
    }
    const polyPoints = currentWall.vertices.map((point) => ({ x: point.x * unitsScale, y: point.y * unitsScale }));
    const linePoints = getParallelLine(polyPoints[0].x - xOffset * unitsScale, polyPoints[0].y - yOffset * unitsScale, polyPoints[1].x - xOffset * unitsScale, polyPoints[1].y - yOffset * unitsScale, lineOffset);

    wallContainerRect.current = useMemo(() => {
        const rect = new fabric.Rect({
            name: 'wallContainerRect_' + currentWall.id,
            //@ts-ignore
            wallId: currentWall.id,
            left: updatedCenter.x,
            top: updatedCenter.y,
            originX: 'center',
            originY: 'center',
            fill: wall.color || '#A3937E',
            height: 10,
            width: currentWall.width * unitsScale,
            hasControls: false,
            angle: currentWall.rotation,
            lockMovementX: true,
            lockMovementY: true,
            lockRotation: true,
            //opacity: 0.1
        });
        canvas.add(rect);

        // textRef.current = new fabric.Text(`${wallWidthFt}"`, {
        //     left: textPos.x,
        //     top: textPos.y,
        //     originX: 'center',
        //     originY: 'center',
        //     fill: 'blue',
        //     fontSize: 10,
        //     backgroundColor: '#FADBD8',
        //     selectable: false
        // });
        // canvas.add(textRef.current);

        initEvents(rect);
        return rect;
    }, []);

    useEffect(() => {
        if (lineRef.current) canvas.remove(lineRef.current);
        if (leftArrowRef.current) canvas.remove(leftArrowRef.current);
        if (rightArrowRef.current) canvas.remove(rightArrowRef.current);
        if (textRef.current) canvas.remove(textRef.current);

        lineRef.current = new fabric.Line([linePoints.x1, linePoints.y1, linePoints.x2, linePoints.y2], {
            stroke: 'black',
            strokeWidth: 0.5,
            selectable: false
        });
        // canvas.add(lineRef.current);

        let leftArrowAngle = 30;
        if (currentWall.id === 'wall2') leftArrowAngle = -30;
        if (currentWall.id === 'wall1') {
            leftArrowAngle = 120;
        }
        if (currentWall.id === 'wall3') {
            leftArrowAngle = 180;
        }

        leftArrowRef.current = new fabric.Triangle({
            left: linePoints.x1,
            top: linePoints.y1,
            fill: 'black',
            strokeWidth: 1,
            originX: 'center',
            originY: 'center',
            width: 5,
            height: 5,
            angle: leftArrowAngle,
            selectable: false
        });
        // canvas.add(leftArrowRef.current);

        let rightArrowAngle = 90;
        if (currentWall.id === 'wall2') rightArrowAngle = -90;
        if (currentWall.id === 'wall1') {
            rightArrowAngle = 180;
        }
        if (currentWall.id === 'wall3') {
            rightArrowAngle = 0;
        }
        rightArrowRef.current = new fabric.Triangle({
            left: linePoints.x2,
            top: linePoints.y2,
            fill: 'black',
            strokeWidth: 1,
            originX: 'center',
            originY: 'center',
            width: 5,
            height: 5,
            angle: rightArrowAngle,
            selectable: false
        });
        // canvas.add(rightArrowRef.current);

        const textPosition = getCenterOfTiltedLine({ x: linePoints.x1, y: linePoints.y1 }, { x: linePoints.x2, y: linePoints.y2 });
        textRef.current = new fabric.Text(` ${wallWidthFt}${wallWidthInchesStr} ft `, {
            left: textPosition.x,
            top: textPosition.y,
            originX: 'center',
            originY: 'center',
            fill: 'black',
            fontSize: 11.5,
            fontFamily: 'Nunito, sans-serif',
            fontWeight: 600,
            backgroundColor: 'white',
            selectable: false
        });
        canvas.add(textRef.current);
    }, [currentWall.vertices])


    let childMeshes: JSX.Element[] = [];
    if (wallContainerRect.current && textRef.current) {
        childMeshes = wall.children.map(child => {
            return <TwoDWindowDoorRenderer
                key={child.id}
                canvas={canvas}
                wallContainerRect={wallContainerRect.current!}
                mesh={child}
                wallId={currentWall.id}
                unitsScale={unitsScale}
                isActive={currentWindowOrDoorId === child.id}
                xOffset={xOffset}
                yOffset={yOffset}
            />
        })
        let addEvents = false;
        if (wallContainerRect.current.left !== updatedCenter.x || wallContainerRect.current.top !== updatedCenter.y) {
            addEvents = true;
        }
        wallContainerRect.current.left = updatedCenter.x;
        wallContainerRect.current.top = updatedCenter.y;
        wallContainerRect.current.width = currentWall.width * unitsScale;
        wallContainerRect.current.angle = currentWall.rotation;
        wallContainerRect.current.set({ fill: wall.color || '#A3937E' });
        wallContainerRect.current.setCoords();

        const textPosition = getCenterOfTiltedLine({ x: linePoints.x1, y: linePoints.y1 }, { x: linePoints.x2, y: linePoints.y2 })
        textRef.current.left = textPosition.x;
        textRef.current.top = textPosition.y;
        textRef.current.fontSize = 12;
        textRef.current.set('text', ` ${wallWidthFt}${wallWidthInchesStr} ft `);
        textRef.current.bringForward();

        canvas.requestRenderAll();
    }
    return <>
        {childMeshes}
    </>;
}

export default TwoDWallRenderer;